import React, { useState } from 'react';

/**
 * Login Form Component
 * Handles user authentication with Supabase
 */
export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Get Supabase client from window (set by auth.js)
      const supabase = window.supabaseClient;
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;

      // Get user profile to store name and pronouns
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('name, pronouns')
        .eq('user_id', data.user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      // Store auth state in localStorage
      const authState = {
        userId: data.user.id,
        email: data.user.email,
        name: profile?.name || '',
        pronouns: profile?.pronouns || '',
        loggedInAt: new Date().toISOString()
      };

      localStorage.setItem('pinkWalletAuth', JSON.stringify(authState));

      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('authStateChanged', { detail: authState }));

      // Redirect to custom URL or home securely
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect');

      const isSafeRedirect = redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//');

      if (isSafeRedirect) {
        window.location.href = redirectUrl;
      } else {
        window.location.href = '/';
      }

    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to log in. Please check your credentials.');
      setIsLoading(false);
    }
  };

  return (
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Log In</h1>
          <p className="text-lg text-gray-600">Welcome back to Pink Wallet</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="your.email@example.com"
                disabled={isLoading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <a href="/forgot-password/" className="text-sm text-brand-700 hover:text-brand-800">
                  Forgot password?
                </a>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-brand-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Logging in...' : 'Log In'}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <a href={`/subscribe/?redirect=${new URLSearchParams(window.location.search).get('redirect') || window.location.pathname}`} className="text-brand-700 hover:text-brand-800 font-medium">
                Sign up
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
